/**
 * Der sevDesk-Block im Admin Center (v12).
 *
 * Der Menüpunkt „sevDesk-Abgleich" ist entfallen: Das Menü führt zu
 * Arbeitsbereichen, und ein Abgleich ist eine anlassbezogene Wartungsaufgabe.
 * Die Anbindung gehört dorthin, wo auch Outlook steht – wer
 * eine Integration pflegt, sucht sie im Admin Center und nicht unter „Kunden".
 *
 * Was an der Anbindung offen ist, steht zusätzlich in der
 * Buchhaltungsvorbereitung. Dort arbeitet Eveline monatlich, und dort fällt
 * eine fehlende Zuordnung auf – nicht in einer Liste, die niemand aufruft.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useKora } from '../zustand';
import type { SevdeskStand } from '../typen';

function zeitpunkt(iso: string | null): string {
  if (!iso) return 'in dieser Sitzung noch nicht';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'unbekannt';
  return d.toLocaleString('de-AT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function SevdeskBlock({ onSchliessen }: { onSchliessen?: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toastZeigen } = useKora();

  const abfrage = useQuery({
    queryKey: ['sevdesk-stand', 'mit-verbindung'],
    /* Der Block zeigt den Verbindungsstand – also wird er auch geprueft. */
    queryFn: () => api.hole<SevdeskStand>('/sevdesk/stand?verbindung=1'),
    staleTime: 30_000,
  });

  const abgleichen = useMutation({
    mutationFn: () => api.sende<{ geholt: number; neu: number; zugeordnet: number; fehler: string | null }>('/sevdesk/abgleichen'),
    onSuccess: (e) => {
      void queryClient.invalidateQueries({ queryKey: ['sevdesk-stand'] });
      void queryClient.invalidateQueries({ queryKey: ['abwicklung-rechnungen'] });
      toastZeigen(
        e.fehler
          ? `sevDesk meldet: ${e.fehler}`
          : `${e.geholt} Rechnungen gelesen, ${e.neu} neu, ${e.zugeordnet} zugeordnet.`,
      );
    },
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Der Abgleich ist fehlgeschlagen.'),
  });

  const d = abfrage.data;

  const hin = (pfad: string) => {
    navigate(pfad);
    onSchliessen?.();
  };

  return (
    <section className="ein-abschnitt ein-trenner">
      <div className="ein-titel">sevDesk</div>

      {!d ? (
        <div className="ein-hinweis">Stand wird geladen …</div>
      ) : (
        <>
          <div className="sv-stand">
            <span className={`sv-punkt ${d.erreichbar ? 'an' : 'aus'}`} aria-hidden="true" />
            <span className="sv-stand-text">
              {d.erreichbar
                ? `verbunden${d.anzahlKontakte !== null ? ` · ${d.anzahlKontakte} Kontakte in sevDesk` : ''}`
                : `nicht verbunden – ${d.meldung}`}
            </span>
            <span className="sv-stand-unter">
              {d.schreibt
                ? 'Kora darf lesen und schreiben'
                : d.lesbar
                  ? 'Kora darf nur lesen – Kontakte nachschlagen, nichts anlegen'
                  : 'kein Token hinterlegt'}
              {' · letzter Rechnungsabgleich: '}
              {zeitpunkt(d.letzterAbgleich)}
            </span>
          </div>

          <div className="sv-aktionen">
            <button
              type="button"
              className="ein-knopf-sek"
              onClick={() => abgleichen.mutate()}
              disabled={abgleichen.isPending || !d.schreibt}
              title={d.schreibt ? 'Rechnungen aus sevDesk holen und zuordnen' : 'Ohne Schreibrecht kann Kora nicht abgleichen'}
            >
              {abgleichen.isPending ? 'Läuft …' : 'Jetzt abgleichen'}
            </button>
          </div>

          {/*
            „Verbindung trennen" steht bewusst nicht hier.

            Der Token kommt aus der Umgebung (SEVDESK_API_TOKEN), nicht aus der
            Datenbank – Kora kann ihn nicht löschen. Ein Knopf, der nichts tut
            oder eine Fehlermeldung liefert, ist schlimmer als der Satz, der
            sagt, wo die Verbindung wirklich hängt.
          */}
          <div className="ein-hinweis">
            {d.tokenAusUmgebung
              ? 'Der Zugang hängt am Token in der Umgebung (SEVDESK_API_TOKEN). Zum Trennen wird der Wert in den Azure-Einstellungen entfernt und die Anwendung neu gestartet – aus Kora heraus geht das nicht.'
              : 'Es ist kein Token hinterlegt. Bis dahin liest Kora keine Kontakte und keine Rechnungen.'}
          </div>

          {/* Was offen ist – jede Zahl führt dorthin, wo man sie abarbeitet. */}
          <div className="sv-offen">
            <button type="button" className="sv-offen-zeile" onClick={() => hin('/kunden?ohneSevdesk=1')}>
              <span className="sv-offen-zahl">{d.ohneKontakt}</span>
              <span className="sv-offen-text">
                {d.ohneKontakt === 1 ? 'Kunde ohne sevDesk-Kontakt' : 'Kunden ohne sevDesk-Kontakt'}
                <span className="sv-offen-wo">im Kundenstamm zuordnen</span>
              </span>
            </button>
            <button type="button" className="sv-offen-zeile" onClick={() => hin('/buchhaltung')}>
              <span className="sv-offen-zahl">{d.rechnungenOhneAuftrag}</span>
              <span className="sv-offen-text">
                {d.rechnungenOhneAuftrag === 1
                  ? 'Rechnung ohne Auftragsbezug'
                  : 'Rechnungen ohne Auftragsbezug'}
                <span className="sv-offen-wo">in der Buchhaltungsvorbereitung</span>
              </span>
            </button>
          </div>
        </>
      )}
    </section>
  );
}
